// app/settings/branches/page.jsx
"use client";
import { useCallback, useEffect, useState } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import BranchModal from "./componants/branchModal";
import branchesApi, { apiError } from "../../../components/api/Branchesapi";

export default function BranchesPage() {
  const { showToast } = useToast();

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---- load ------------------------------------------------------------- */
  const fetchBranches = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { items } = await branchesApi.list();
      setBranches(items);
    } catch (err) {
      setLoadError(apiError(err, "Couldn't load branches"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  /* ---- filters ---------------------------------------------------------- */
  const filtered = branches.filter((b) => {
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${b.name} ${b.code} ${b.city} ${b.state}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (statusFilter === "active" && !b.isActive) return false;
    if (statusFilter === "inactive" && b.isActive) return false;
    return true;
  });

  /* ---- handlers --------------------------------------------------------- */
  const handleAdd = () => {
    setEditingBranch(null);
    setModalOpen(true);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setModalOpen(true);
  };

  const handleSave = async (data, id) => {
    setIsSaving(true);
    try {
      if (id) {
        await branchesApi.update(id, data);
        showToast("Branch updated", "success");
      } else {
        await branchesApi.create(data);
        showToast("Branch created", "success");
      }
      await fetchBranches();
      setModalOpen(false);
      setEditingBranch(null);
      return true;
    } catch (err) {
      showToast(apiError(err, "Couldn't save this branch"), "error");
      return false; // keep the modal open, don't lose the input
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (branch) => {
    try {
      const res = await branchesApi.toggleStatus(branch.id);
      showToast(res.message, "success");
      await fetchBranches();
    } catch (err) {
      showToast(apiError(err, "Couldn't change the status"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await branchesApi.remove(deleteConfirm.id, reassignTo || undefined);
      showToast(`${deleteConfirm.name} deleted`, "success");
      setDeleteConfirm(null);
      setReassignTo("");
      await fetchBranches();
    } catch (err) {
      showToast(apiError(err, "Couldn't delete this branch"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const reassignOptions = branches.filter((b) => b.id !== deleteConfirm?.id);

  /* ---- render ----------------------------------------------------------- */
  return (
    <CrmLayout title="Branches">
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Branches</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {branches.length} {branches.length === 1 ? "branch" : "branches"}
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Add Branch
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code or city..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : loadError ? (
            <div className="py-14 px-5 text-center space-y-3">
              <i className="ri-wifi-off-line text-3xl text-gray-300 block" />
              <p className="text-sm text-gray-600">{loadError}</p>
              <button
                onClick={fetchBranches}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Try again
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <i className="ri-map-pin-line text-4xl text-gray-300 block" />
              <p className="text-gray-500 text-sm">
                {branches.length === 0
                  ? "No branches yet. Add your head office to get started."
                  : "No branches match these filters."}
              </p>
              {branches.length === 0 && (
                <button
                  onClick={handleAdd}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-semibold text-white transition-colors"
                >
                  Add the first branch
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {["Branch", "Code", "Location", "Phone", "Users", "Status", "Actions"].map(
                      (h, i) => (
                        <th
                          key={h}
                          className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                            i === 6 ? "text-right" : "text-left"
                          }`}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                            <i className="ri-map-pin-line" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 whitespace-nowrap">{b.name}</p>
                            {b.address && (
                              <p className="text-xs text-gray-400 max-w-[240px] truncate">
                                {b.address}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {b.code || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {[b.city, b.state].filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {b.phone || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap">
                          {b.userCount} {b.userCount === 1 ? "user" : "users"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggle(b)}
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                            b.isActive
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}
                          title="Click to change"
                        >
                          {b.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(b)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteConfirm(b);
                              setReassignTo("");
                            }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Branch Modal */}
        {modalOpen && (
          <BranchModal
            branch={editingBranch}
            isSaving={isSaving}
            onClose={() => {
              setModalOpen(false);
              setEditingBranch(null);
            }}
            onSave={handleSave}
          />
        )}

        {/* Delete Confirm */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm shadow-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <i className="ri-delete-bin-line text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Delete {deleteConfirm.name}?</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {deleteConfirm.userCount > 0
                      ? `${deleteConfirm.userCount} ${
                          deleteConfirm.userCount === 1 ? "user is" : "users are"
                        } assigned. Pick where they move to.`
                      : "This action cannot be undone."}
                  </p>
                </div>
              </div>

              {deleteConfirm.userCount > 0 && (
                <select
                  value={reassignTo}
                  onChange={(e) => setReassignTo(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                >
                  <option value="">Choose a branch…</option>
                  {reassignOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}

              <div className="flex items-center gap-2.5 justify-end pt-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting || (deleteConfirm.userCount > 0 && !reassignTo)}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-medium text-white transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    "Delete Branch"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}